import { useMutation } from '@tanstack/react-query';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '@/app/providers/auth/authContext';
import { routePaths } from '@/app/router/routePaths';
import { areSettingsEditable, type Team, type TeamProfile } from '@/core/schemas';
import { useTournament } from '@/features/tournament/useTournament';
import { updateTeamProfile } from '@/infrastructure/repositories/teamRepository';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

import { TeamForm } from './TeamForm';
import { describeError } from './describeError';
import { teamToFormValues } from './teamFormModel';
import { useMyTeam } from './useMyTeam';

export function TeamProfilePage() {
  usePageTitle('Mein Team');
  const { session } = useAuth();
  const tournament = useTournament();
  const myTeam = useMyTeam();
  const [isEditing, setIsEditing] = useState(false);

  const update = useMutation({
    mutationFn: (input: { team: Team; profile: TeamProfile }) => {
      if (session.status !== 'authenticated') {
        throw new Error('Nicht angemeldet');
      }
      return updateTeamProfile({
        team: input.team,
        profile: input.profile,
      });
    },
    onSuccess: () => {
      setIsEditing(false);
    },
  });

  if (tournament.isLoading || myTeam.isLoading) {
    return <LoadingScreen label="Teamdaten werden geladen …" />;
  }

  if (tournament.error !== null) {
    return <PageState title="Turnier nicht verfügbar" description={tournament.error.message} />;
  }

  if (myTeam.data === null) {
    return <Navigate to={routePaths.team.registration} replace />;
  }

  if (myTeam.data === undefined || tournament.data === undefined) {
    return <LoadingScreen />;
  }

  const team = myTeam.data;
  const { settings, status } = tournament.data;
  const canEdit = areSettingsEditable(status);

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-brand text-xs font-semibold tracking-widest uppercase">
            {team.fireDepartment}
          </p>
          <h1 className="text-2xl font-bold tracking-tight">{team.name}</h1>
        </div>

        {canEdit && !isEditing && (
          <Button
            variant="secondary"
            size="md"
            onClick={() => {
              setIsEditing(true);
            }}
          >
            Bearbeiten
          </Button>
        )}
      </header>

      {!canEdit && (
        <Alert tone="info">
          Das Turnier läuft bereits. Änderungen an den Teamdaten sind nur noch über die
          Turnierleitung möglich.
        </Alert>
      )}

      {update.isSuccess && !isEditing && (
        <Alert tone="success">Die Änderungen wurden gespeichert.</Alert>
      )}

      {isEditing ? (
        <div className="flex flex-col gap-4">
          <TeamForm
            initialValues={teamToFormValues(team)}
            songMaxDurationSeconds={settings.registration.songMaxDurationSeconds}
            submitLabel="Änderungen speichern"
            isSubmitting={update.isPending}
            errorMessage={describeError(update.error)}
            onSubmit={(profile) => {
              update.mutate({ team, profile });
            }}
          />
          <Button
            variant="ghost"
            size="md"
            className="self-start"
            onClick={() => {
              setIsEditing(false);
              update.reset();
            }}
          >
            Abbrechen
          </Button>
        </div>
      ) : (
        <TeamSummary team={team} />
      )}
    </div>
  );
}

function TeamSummary({ team }: { team: Team }) {
  const { walkOnSong } = team;
  const duration = walkOnSong.endSeconds - walkOnSong.startSeconds;

  return (
    <dl className="border-line grid gap-px overflow-hidden rounded-xl border sm:grid-cols-2">
      <Entry label="Feuerwehr" value={team.fireDepartment} />
      <Entry label="Teamname" value={team.name} />
      <Entry
        label="Einlaufsong"
        value={
          walkOnSong.artist === undefined
            ? walkOnSong.title
            : `${walkOnSong.title} — ${walkOnSong.artist}`
        }
      />
      <Entry
        label="Ausschnitt"
        value={`${walkOnSong.startSeconds}–${walkOnSong.endSeconds} s (${duration} s)`}
      />
    </dl>
  );
}

function Entry({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface p-4">
      <dt className="text-fg-subtle text-xs tracking-wide uppercase">{label}</dt>
      <dd className="mt-1 text-sm">{value}</dd>
    </div>
  );
}
